import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CircularProgressProps,
  Grid,
  styled,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import CampaignCard from "components/campaigncard";
import InterviewCard from "components/interviewcard";
import { useAuth } from "hooks/useUser";
import { useMemo } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";

async function getDashboard() {
  const { data } = await axios.get("/agent/dashboard");
  return data;
}

function Dashboard() {
  const {
    data = {
      balance: 0,
      pendingBalance: 0,
      activeCampaigns: [],
      newCampaigns: [],
      chart: [],
    },
  } = useQuery<{
    activeCampaigns: Campaign[];
    newCampaigns: Campaign[];
    balance: number;
    pendingBalance: number;
    chart: {
      date: string;
      count: number;
    }[];
  }>(["AGENT_DASHBOARD"], getDashboard, {
    refetchInterval: 50_000,
    cacheTime: 50_000,
    staleTime: 50_000,
  });

  const state = useMemo(
    () => ({
      options: {
        chart: {
          id: "apexchart-example",
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: data.chart.map((c) => c.date),
        },
        yaxis: {
          min: 0,
        },
        title: {
          text: "Interviews per day",
        },
      },
      series: [
        {
          name: "Campaigns",
          data: data.chart.map((c) => c.count),
        },
      ],
    }),
    [data]
  );
  const { data: user } = useAuth();

  return (
    <Box
      sx={{
        maxWidth: "100%",
        px: 2,
      }}>
      <Box
        sx={{
          display: "grid",
          mb: 4,
          mt: 2,
          gridTemplateColumns: "auto auto auto",
          gap: 4,
          "@media (max-width: 1280px)": {
            gridTemplateColumns: "1fr",
          },
          "@media (max-width: 600px)": {
            gridTemplateColumns: "1fr",
          },
        }}>
        <Box
          sx={{
            p: "20px",
            bgcolor: "#3056D3",
            borderRadius: "10px",
            color: "#fff",
          }}>
          <Typography fontWeight="600" component="h2">
            Ongoing Campaigns
          </Typography>
          <Typography variant="h5" fontWeight="600" component="p" my={3}>
            3
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}>
            <CircularProgressWithLabel value={75} />
            <Typography fontWeight="600" component="p">
              Completed
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            bgcolor: "#F5F5F7",
            borderRadius: "10px",
            px: "20px",
          }}>
          <Chart options={state.options} series={state.series} type="area" width={"100%"} />
        </Box>
        <Box
          sx={{
            bgcolor: "#021658",
            backgroundImage: "url('/fingerprint.png')",
            backgroundRepeat: "no-repeat",
            borderRadius: "10px",
            p: "20px",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
          }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography fontWeight="600" component="h2">
              Wallet Balance
            </Typography>
            <Typography fontWeight="600" fontSize={32} component="p">
              NGN {data.balance.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
              my: 2,
            }}>
            <Chip
              sx={{
                borderRadius: "10px",
              }}
              label="Pending"
              color="warning"
              size="medium"
            />
            <Typography fontWeight="600" fontSize={20} component="p">
              {} {data.pendingBalance.toLocaleString()}
            </Typography>
          </Box>
          <WithdrawButton
            disabled
            variant="outlined"
            size="large"
            sx={{
              mt: "auto",
            }}
            fullWidth>
            Withdraw
          </WithdrawButton>
        </Box>
      </Box>
      {!user.countryName && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" component={Link} to="/setup">
              Fill Profile
            </Button>
          }>
          Please complete your profile to start earning
        </Alert>
      )}
      {user.countryName && !user.completedProfile && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" component={Link} to="/setup/two">
              Complete Profile
            </Button>
          }>
          Please complete your profile to increase chances of getting interviews
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 2 }}>
        <Typography fontSize={"20px"} fontWeight="600" component="h2">
          Your active campaigns
        </Typography>
        <Button variant="text" size="large">
          View all
        </Button>
      </Box>
      <Grid container spacing={3}>
        {data.activeCampaigns.map((campaign) => (
          <Grid item xs={12} sm={12} md={6} lg={4} key={campaign.id}>
            <InterviewCard key={campaign.id} campaign={campaign} />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 2 }}>
        <Typography display="flex" alignItems="center" gap={2} fontSize={"20px"} fontWeight="600" component="h2">
          <Chip
            label="New"
            color="warning"
            size="medium"
            sx={{
              borderRadius: "10px",
              color: "#fff",
            }}
          />
          Campaigns
        </Typography>
        <Button variant="text" size="large">
          View all
        </Button>
      </Box>{" "}
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr",
        }}>
        <Box sx={{ display: "flex", gap: 4, overflowX: "auto", width: "100%", maxWidth: "100%" }}>
          {data.newCampaigns.map((campaign) => (
            <CampaignCard key={campaign.id} campaign={campaign} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default Dashboard;

function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress
        size={80}
        thickness={2}
        sx={{
          color: "#fff",
        }}
        variant="determinate"
        {...props}
        color="info"
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Typography p={2} component="div" color="white">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const WithdrawButton = styled(Button)({
  background: "rgba(42, 62, 124, 0.72)",
  textTransform: "none",
});
